import {useCallback, useMemo} from 'react'

import {ListProps} from './List'

/** В таком виде элементы списка передаются в GWC и принимаются обработчиком событий перед распаковкой. Деталь внутренней реализации на которую не стоит завязываться. */
type WrappedItem<T> = {
  id: string
  value: T
}

// Хук для оборачивания адекватного представления данных в формат, который понимает react-aria (и, соответственно, GWC)
export const useWrappedListItems = <T>({
  items,
  getId,
  selectedItemId,
  onItemChange,
}: Pick<ListProps<T>, 'items' | 'getId' | 'selectedItemId' | 'onItemChange'>) => {
  /** Поскольку react-aria под капотом использует WeakMap, при работе со списком примитивных значений приложение падает. Во избежание этого - оборачиваем. */
  const wrappedItems = useMemo<Array<WrappedItem<T>>>(() => {
    return items.map(item => ({id: getId(item), value: item}))
  }, [items, getId])

  const selection = useMemo(() => {
    return !!selectedItemId ? [selectedItemId] : []
  }, [selectedItemId])

  const onSelectionChange = useCallback(
    (newSelection: 'all' | Set<PropertyKey>) => {
      if (newSelection !== 'all' && newSelection.size === 0) {
        onItemChange?.(undefined)
        return
      }

      if (newSelection === 'all' || newSelection.size !== 1) {
        console.error('Пытались выбрать: ', newSelection)
        // Несмотря на тип данных, react-aria гарантирует что в рантайме это всегда будет Set с одним элементом, если selectionMode="single".
        // Подробнее: https://react-spectrum.adobe.com/react-stately/selection.html#single-selection
        throw new Error('Предпринята попытка выбрать больше одного элемента в списке с единичным выбором.')
      }

      const newKey = [...newSelection][0] as PropertyKey // выше проверили что 1 элемент точно есть
      const newItem = wrappedItems.find(item => item.id === newKey)
      onItemChange?.(newItem?.value)
    },
    [onItemChange, wrappedItems],
  )

  return {wrappedItems, selection, onSelectionChange}
}
